import React, { useEffect, useState } from "react";
import { ArrowLeft, Mail, Calendar, Check, Trash2, Phone, Building } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { api } from "../api";

const STATUS_OPTS = [
  { value: "new", label: "New" },
  { value: "contacted", label: "Contacted" },
  { value: "confirmed", label: "Confirmed" },
  { value: "closed", label: "Closed" },
];

const statusColors = {
  new: "bg-blue-500/15 border-blue-500/40 text-blue-300",
  contacted: "bg-yellow-500/15 border-yellow-500/40 text-yellow-300",
  confirmed: "bg-green-500/15 border-green-500/40 text-green-300",
  closed: "bg-white/5 border-white/10 text-white/40",
};

const AdminBookings = () => {
  const navigate = useNavigate();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState("all");
  const [expanded, setExpanded] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/bookings");
      setBookings(r.data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    window.scrollTo(0, 0);
    load();
  }, []);

  const updateStatus = async (id, status) => {
    await api.patch(`/bookings/${id}/status`, { status });
    await load();
  };

  const removeBooking = async (id) => {
    if (!window.confirm("Delete this booking request?")) return;
    await api.delete(`/bookings/${id}`);
    await load();
  };

  const filtered = filterStatus === "all" ? bookings : bookings.filter((b) => b.status === filterStatus);

  return (
    <div className="min-h-screen bg-[#121212] pt-24 pb-20" data-testid="admin-bookings">
      <div className="max-w-6xl mx-auto px-6">
        <div className="flex items-center gap-4 mb-10">
          <button
            onClick={() => navigate("/admin")}
            className="flex items-center gap-2 text-white/40 hover:text-gold transition-colors text-xs font-body tracking-wider uppercase"
          >
            <ArrowLeft size={13} />
            <span>Dashboard</span>
          </button>
          <div className="h-px flex-1 bg-gold/10" />
        </div>

        <div className="flex items-center gap-3 mb-4">
          <div className="h-px w-8 bg-gold/60" />
          <span className="font-body text-gold text-[0.65rem] font-semibold tracking-[0.4em] uppercase">Booking Requests</span>
        </div>
        <h1 className="font-display font-light text-5xl text-white mb-10">Inbox</h1>

        {/* Filters */}
        <div className="flex flex-wrap gap-2 mb-8">
          <button
            onClick={() => setFilterStatus("all")}
            className={`specialty-tag cursor-pointer ${filterStatus === "all" ? "bg-gold/20 border-gold text-gold" : ""}`}
          >
            All ({bookings.length})
          </button>
          {STATUS_OPTS.map((s) => {
            const cnt = bookings.filter((b) => b.status === s.value).length;
            return (
              <button
                key={s.value}
                onClick={() => setFilterStatus(s.value)}
                className={`specialty-tag cursor-pointer ${filterStatus === s.value ? "bg-gold/20 border-gold text-gold" : ""}`}
              >
                {s.label} ({cnt})
              </button>
            );
          })}
        </div>

        {loading ? (
          <div className="py-24 flex items-center justify-center">
            <div className="w-10 h-10 border-2 border-gold/20 border-t-gold rounded-full animate-spin" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="glass rounded-sm py-20 text-center">
            <Mail size={40} className="text-white/10 mx-auto mb-4" />
            <p className="font-body text-white/30 text-sm">No booking requests in this view.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {filtered.map((b) => (
              <div key={b.id} className="glass rounded-sm overflow-hidden" data-testid={`booking-${b.id}`}>
                <div
                  onClick={() => setExpanded(expanded === b.id ? null : b.id)}
                  className="px-6 py-4 flex flex-col md:flex-row md:items-center gap-4 cursor-pointer hover:bg-white/[0.02]"
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 flex-wrap">
                      <h3 className="font-display text-lg text-white">{b.name}</h3>
                      <span className={`specialty-tag ${statusColors[b.status]}`}>{b.status}</span>
                      {b.artistName && <span className="specialty-tag">→ {b.artistName}</span>}
                    </div>
                    <div className="flex items-center gap-4 mt-1 text-xs font-body text-white/40">
                      <span className="flex items-center gap-1.5"><Mail size={11} />{b.email}</span>
                      {b.phone && <span className="flex items-center gap-1.5"><Phone size={11} />{b.phone}</span>}
                      {b.company && <span className="flex items-center gap-1.5"><Building size={11} />{b.company}</span>}
                      <span className="flex items-center gap-1.5"><Calendar size={11} />{new Date(b.created_at).toLocaleDateString()}</span>
                    </div>
                  </div>
                </div>

                {expanded === b.id && (
                  <div className="px-6 py-5 border-t border-gold/10 bg-black/30 space-y-4">
                    {b.eventType && (
                      <div>
                        <div className="font-body text-gold text-[0.6rem] tracking-widest uppercase mb-1">Event Type</div>
                        <div className="font-body text-white/70 text-sm">{b.eventType}</div>
                      </div>
                    )}
                    {b.eventDate && (
                      <div>
                        <div className="font-body text-gold text-[0.6rem] tracking-widest uppercase mb-1">Event Date</div>
                        <div className="font-body text-white/70 text-sm">{b.eventDate}</div>
                      </div>
                    )}
                    <div>
                      <div className="font-body text-gold text-[0.6rem] tracking-widest uppercase mb-1">Message</div>
                      <p className="font-body text-white/70 text-sm leading-relaxed whitespace-pre-wrap">{b.message}</p>
                    </div>

                    <div className="flex flex-wrap gap-2 pt-2">
                      <span className="font-body text-white/30 text-[0.6rem] tracking-widest uppercase flex items-center mr-2">Update Status:</span>
                      {STATUS_OPTS.map((s) => (
                        <button
                          key={s.value}
                          onClick={(e) => { e.stopPropagation(); updateStatus(b.id, s.value); }}
                          className={`specialty-tag cursor-pointer ${b.status === s.value ? "bg-gold/20 border-gold text-gold" : ""}`}
                          data-testid={`booking-${b.id}-status-${s.value}`}
                        >
                          {b.status === s.value && <Check size={9} className="mr-1" />}
                          {s.label}
                        </button>
                      ))}
                      <button
                        onClick={(e) => { e.stopPropagation(); removeBooking(b.id); }}
                        className="specialty-tag ml-auto cursor-pointer hover:text-red-400 hover:border-red-500/40"
                      >
                        <Trash2 size={10} className="mr-1" />
                        Delete
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminBookings;
